import { useCallback, useMemo, useState } from 'react';

import {
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ScrollableTable } from './table/scrollable-table';
import { PendingInviteBulkResultStatus } from '../../../../backend/src/auth/pending-matter-invite/dto/pending-invite-bulk-add.response.dto';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { PermissionGate } from '../../hooks/useHasPermission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddPendingInvites } from '@/api/mutations/useAddPendingInvites';
import { useDeletePendingInvite } from '@/api/mutations/useDeletePendingInvite';
import { useUpdatePendingInvite } from '@/api/mutations/useUpdatePendingInvite';
import useGetPendingInvites from '@/api/queries/useGetPendingInvites';
import { Role } from '@/api/queries/useGetRoles';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { InviteUserDialog } from '@/new/components/invite-user-dialog';
import { PendingMatterInvitesDialog } from '@/new/components/pending-matter-invites-dialog';
import { Button } from '@/new/components/shadcn/button';

interface UserTableProps {
  users: User[];
  addedUsers: User[];
  onUserChange: (users: User[]) => void;
  matterCreatorId?: string;
  roles: Role[];
  canManageOverrides: boolean;
  overridesEnabled: boolean;
  canRemoveInvites: boolean;
}

export function UserTable({
  users,
  addedUsers,
  onUserChange,
  matterCreatorId,
  roles,
  canManageOverrides,
  overridesEnabled,
  canRemoveInvites,
}: UserTableProps) {
  const { matterId } = useParams();
  if (!matterId) return;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isPendingInvitesDialogOpen, setIsPendingInvitesDialogOpen] = useState(false);

  const { user } = useUserContext();

  // Pending invites
  const { data: pendingInvites } = useGetPendingInvites({ matterId });
  const { mutateAsync: addPendingInvites } = useAddPendingInvites({ matterId });
  const { mutate: deletePendingInvite } = useDeletePendingInvite({ matterId });
  const { mutateAsync: updatePendingInvite } = useUpdatePendingInvite({ matterId });

  const handleRemovePendingInvite = (inviteId: string) => {
    deletePendingInvite({ inviteId });
  };

  // Separate users into added and available
  const { availableUsers } = useMemo(() => {
    const added: User[] = [];
    const available: User[] = [];

    users.forEach((u) => {
      if (addedUsers.some((au) => au.id === u.id) || u.id === matterCreatorId) {
        added.push(u);
      } else {
        available.push(u);
      }
    });

    return { availableUsers: available };
  }, [users, addedUsers, matterCreatorId]);
  const existingMatterUserEmails = useMemo(() => {
    const creator = users.find(({ id }) => id === matterCreatorId);
    return [...addedUsers.map(({ email }) => email), ...(creator ? [creator.email] : [])];
  }, [addedUsers, matterCreatorId, users]);

  const handleAddUser = (user: User) => {
    trackCustomEvent(TrackingEventsV2.MatterAddTeamMemberOnCreator, { userId: user.id });
    onUserChange([...addedUsers, user]);
  };

  // If the entered email already belongs to an org user, add them to the matter directly;
  // otherwise send a pending invite for someone who isn't on Wexler yet.
  const handleInviteOrAddUser = useCallback(
    async (emails: string[]) => {
      trackCustomEvent(TrackingEventsV2.MatterInviteUserNoAccount, {
        userId: user?.id,
        orgId: user?.organisationId,
        inviteCount: emails.length,
      });
      const response = await addPendingInvites(emails);
      const failed = response.results.filter(({ status }) => status === PendingInviteBulkResultStatus.Failed).length;
      if (failed > 0) {
        toast.error(
          `${response.results.length - failed} of ${response.results.length} users invited. ${failed} ${failed === 1 ? 'invitation' : 'invitations'} failed.`,
        );
      } else {
        toast.success(`${response.results.length} ${response.results.length === 1 ? 'user' : 'users'} invited`);
      }
      return response;
    },
    [addPendingInvites, user?.id, user?.organisationId],
  );

  const availableTable = useReactTable({
    data: availableUsers,
    columns: [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div
              className={`text-left lowercase ${isDisabled ? 'text-gray-400' : ''}`}
              data-testid={`available-user-email-${row.original.id}`}
            >
              {row.getValue('email')}
              {isDisabled && <span className="ml-2 text-xs text-gray-500">(disabled)</span>}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div className="flex justify-end">
              <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAddUser(row.original)}
                  disabled={isDisabled}
                  data-testid={`add-user-${row.original.id}`}
                >
                  <Icon className="lucide--plus" />
                  Add
                </Button>
              </PermissionGate>
            </div>
          );
        },
      },
    ],
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      globalFilter,
    },
  });

  const tableButtons = (
    <>
      <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
        <Button data-testid="invite-user-button" onClick={() => setIsInviteDialogOpen(true)}>
          <Icon className="lucide--user-plus" />
          Invite Users
        </Button>
      </PermissionGate>
      <Button
        data-testid="pending-invites-button"
        disabled={!pendingInvites?.length}
        onClick={() => setIsPendingInvitesDialogOpen(true)}
      >
        <Icon className="lucide--hourglass" /> Pending invites ({pendingInvites?.length ?? 0})
      </Button>

      <InviteUserDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        existingMatterUserEmails={existingMatterUserEmails}
        organisationUserEmails={users.map(({ email }) => email)}
        onInvite={handleInviteOrAddUser}
      />

      <PendingMatterInvitesDialog
        open={isPendingInvitesDialogOpen}
        onOpenChange={setIsPendingInvitesDialogOpen}
        invites={pendingInvites ?? []}
        roles={roles}
        canManageRoles={canManageOverrides && overridesEnabled}
        roleEditingDisabledReason={
          !overridesEnabled
            ? 'Enable Matter-level permission overrides to assign a role for this Matter.'
            : !canManageOverrides
              ? 'Only administrators can assign roles for this Matter.'
              : undefined
        }
        canRemoveInvites={canRemoveInvites}
        onRoleChange={(inviteId, roleId) => updatePendingInvite({ inviteId, roleId })}
        onRemove={handleRemovePendingInvite}
      />
    </>
  );

  return (
    <ScrollableTable
      table={availableTable}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      plural="available users"
      additionalButtons={tableButtons}
      className="-mx-8"
    />
  );
}
